/**
 * Message Care Team in the patient app (Treatment Plan spec v1.4, S-10, S-11;
 * canvas board 8). Messages lists the conversations; New message goes topic,
 * common question, instant answer ("That answers it" or "Message my care
 * team"), then a short compose screen; the conversation shows the replies.
 *
 * The patient sees only what the API's toPatientThread returns (MS-011).
 * Symptoms show the emergency banner first (MS-006). A treatment plan waiting
 * for the patient's decision is asked about on the plan page (MS-004). Text
 * alerts are not promised here: they switch on with the Twilio BAA.
 */

import { useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { portalBlob, portalRequest, portalUpload } from '../../lib/portal';
import { PhotoThumb } from '../../components/PhotoThumb';
import { usePortalQuery } from './shell';
import { Calendar, Capsule, ChatBubble, Clipboard, DocLine, Flask } from './icons';
import { Chevron, FOREST_SOFT, LINE, MUTED, Screen, primary, secondary } from './PlanQuestions';

interface ThreadView {
  id: string;
  topic: string;
  status: string;
  canReply: boolean;
  unread: number;
  messages: Array<{ from: string; body: string; at: string; photos?: Photo[] }>;
  updatedAt?: string;
  /** MS-014: photos can be added in this conversation. */
  photos?: boolean;
}
interface Photo {
  id: string;
  mime: string;
}
interface MessagesHome {
  clinicName: string;
  replyTimeText: string | null;
  careHoursText: string | null;
  footer: string;
  unreadThreads: number;
  threads: ThreadView[];
}
interface Topics {
  footer: string;
  topics: Array<{
    key: string;
    label: string;
    sub: string | null;
    clinical: boolean;
    banner: string | null;
    /** MS-014: photos can be added on this topic. */
    photos: boolean;
    planQuestionsFor: string | null;
    questions: Array<{ id: string; question: string; answer: string | null }>;
  }>;
}
type Topic = Topics['topics'][number];

const NAVY = '#1c2b3a';
const ALERT = '#b42318';

const time = (iso: string) => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
const when = (iso: string) => {
  const date = new Date(iso);
  const today = new Date();
  const days = Math.floor((today.setHours(0, 0, 0, 0) - new Date(iso).setHours(0, 0, 0, 0)) / 86_400_000);
  if (days === 0) return `Today · ${time(iso)}`;
  if (days === 1) return 'Yesterday';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

function TopicIcon({ topic }: { topic: Pick<Topic, 'key' | 'clinical'> }) {
  const size = 18;
  const icon: Record<string, ReactNode> = {
    medication: <Capsule size={size} />,
    plan: <Clipboard size={size} />,
    billing: <DocLine size={size} />,
    labs: <Flask size={size} />,
    appointments: <Calendar size={size} />,
    other: <ChatBubble size={size} />,
  };
  return (
    <span
      aria-hidden="true"
      className="grid h-9 w-9 shrink-0 place-items-center rounded-[9px] text-[15px] font-bold"
      style={topic.clinical ? { background: '#fbe4e1', color: ALERT } : { background: FOREST_SOFT, color: '#1b3f35' }}
    >
      {topic.clinical ? '!' : icon[topic.key] ?? <ChatBubble size={size} />}
    </span>
  );
}

/* ------------------------------------------------------------------ *
 * S-10: Messages
 * ------------------------------------------------------------------ */

export function PortalMessages() {
  const navigate = useNavigate();
  const home = usePortalQuery<MessagesHome>('messages-home', '/messages');
  const d = home.data;
  const lead = d
    ? [d.replyTimeText ? `${d.clinicName} replies ${d.replyTimeText}.` : `${d.clinicName} replies here.`, d.careHoursText ? `Care hours: ${d.careHoursText}.` : null]
        .filter(Boolean)
        .join(' ')
    : '';
  return (
    <Screen
      title="Messages"
      onBack={() => navigate('/portal')}
      testId="portal-messages"
      footer={
        <>
          <button type="button" className={primary} onClick={() => navigate('/portal/messages/new')}>
            New message
          </button>
          <p className="mt-2 text-center text-[12.5px]" style={{ color: MUTED }}>If this is an emergency, call 911.</p>
        </>
      }
    >
      <h1 className="mt-1 font-display text-[24px] font-semibold" style={{ color: NAVY }}>Your care team</h1>
      {lead && <p className="mt-1 text-[13.5px] leading-normal" style={{ color: MUTED }}>{lead}</p>}
      {home.isLoading && <p className="mt-4 text-[13.5px]" style={{ color: MUTED }}>Loading</p>}
      {home.error && <p className="mt-4 text-[13.5px] text-alert">Could not load your messages.</p>}
      {d && d.threads.length === 0 && (
        <p className="mt-5 rounded-[12px] border bg-white px-4 py-5 text-[14px]" style={{ borderColor: LINE, color: MUTED }}>
          No messages yet. Tap New message to write to your care team.
        </p>
      )}
      <div className="mt-4 flex flex-col gap-[10px]" data-testid="thread-list">
        {d?.threads.map((t) => {
          const last = t.messages.at(-1);
          return (
            <Link key={t.id} to={`/portal/messages/${t.id}`} className="block rounded-[12px] border bg-white px-4 py-3" style={{ borderColor: LINE }}>
              <span className="flex items-center justify-between gap-2">
                <span className="text-[12.5px] font-semibold" style={{ color: t.unread ? '#1b3f35' : MUTED }}>{t.status}</span>
                {t.unread > 0 && <span aria-label="Unread" className="h-2 w-2 rounded-full" style={{ background: ALERT }} />}
              </span>
              <span className="mt-0.5 block font-display text-[17px] font-semibold" style={{ color: NAVY }}>{t.topic}</span>
              {last && <span className="mt-0.5 block truncate text-[13.5px]" style={{ color: '#3f4a55' }}>{last.body}</span>}
              {last && <span className="mt-1 block text-[12px]" style={{ color: MUTED }}>{last.from === 'You' ? when(last.at) : `${last.from} · ${when(last.at)}`}</span>}
            </Link>
          );
        })}
      </div>
    </Screen>
  );
}

/* ------------------------------------------------------------------ *
 * S-11: New message
 * ------------------------------------------------------------------ */

/**
 * MS-014: up to three JPEG, PNG or HEIC photos, 10 MB each, on the symptoms topic. Each is uploaded as it is chosen,
 * so the patient sees what will be sent, and can remove it before sending.
 */
function PhotoPicker({ photos, onChange }: { photos: Photo[]; onChange: (next: Photo[]) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const add = async (files: FileList | null) => {
    if (!files?.length) return;
    setError(null);
    setBusy(true);
    let next = photos;
    try {
      for (const file of Array.from(files).slice(0, 3 - photos.length)) {
        const form = new FormData();
        form.append('photo', file);
        next = [...next, await portalUpload<Photo>('/attachments', form)];
        onChange(next);
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'That photo could not be added.');
    } finally {
      setBusy(false);
    }
  };
  const remove = async (id: string) => {
    await portalRequest(`/attachments/${id}`, { method: 'DELETE' }).catch(() => undefined);
    onChange(photos.filter((p) => p.id !== id));
  };
  return (
    <div className="mt-3" data-testid="photo-picker">
      <div className="flex flex-wrap items-center gap-2">
        {photos.map((p, i) => (
          <span key={p.id} className="relative">
            <PhotoThumb load={() => portalBlob(`/attachments/${p.id}`)} mime={p.mime} label={`Photo ${i + 1}`} size={72} />
            <button type="button" aria-label={`Remove photo ${i + 1}`} onClick={() => void remove(p.id)} className="absolute -right-1.5 -top-1.5 grid h-6 w-6 place-items-center rounded-full bg-white text-[14px] shadow" style={{ color: NAVY }}>
              ×
            </button>
          </span>
        ))}
        {photos.length < 3 && (
          <label className={`grid h-[72px] w-[72px] cursor-pointer place-items-center rounded-[10px] border border-dashed text-center text-[12px] font-semibold ${busy ? 'opacity-50' : ''}`} style={{ borderColor: '#b9cfca', color: '#1b3f35' }}>
            {busy ? 'Adding' : 'Add photo'}
            <input type="file" accept="image/jpeg,image/png,image/heic,.heic" multiple className="sr-only" disabled={busy} onChange={(e) => { void add(e.target.files); e.target.value = ''; }} data-testid="photo-input" />
          </label>
        )}
      </div>
      <p className="mt-1.5 text-[12px]" style={{ color: error ? ALERT : MUTED }} role={error ? 'alert' : undefined}>
        {error ?? 'Up to 3 photos, JPEG, PNG or HEIC. Only your care team sees them.'}
      </p>
    </div>
  );
}

function MessagePhotos({ photos }: { photos?: Photo[] }) {
  if (!photos?.length) return null;
  return (
    <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="message-photos">
      {photos.map((p, i) => <PhotoThumb key={p.id} load={() => portalBlob(`/attachments/${p.id}`)} mime={p.mime} label={`Photo ${i + 1}`} size={88} />)}
    </div>
  );
}

export function PortalNewMessage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const menu = usePortalQuery<Topics>('message-topics', '/messages/topics');
  const [topicKey, setTopicKey] = useState<string | null>(null);
  const [questionId, setQuestionId] = useState<string | null>(null);
  const [composing, setComposing] = useState(false);
  const [body, setBody] = useState('');
  const [call, setCall] = useState(false);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const topic = menu.data?.topics.find((t) => t.key === topicKey) ?? null;
  const question = topic?.questions.find((q) => q.id === questionId) ?? null;

  const send = useMutation({
    mutationFn: () => portalRequest<ThreadView>('/messages', { method: 'POST', body: { topicKey, questionId, body, contactPref: call ? 'call' : 'portal', photoIds: photos.map((p) => p.id) } }),
    onSuccess: (thread) => {
      void qc.invalidateQueries({ queryKey: ['portal'] });
      navigate(`/portal/messages/${thread.id}`, { replace: true });
    },
  });
  const helpful = useMutation({
    mutationFn: () => portalRequest('/messages/instant-answer', { method: 'POST', body: { questionId } }),
    onSettled: () => navigate('/portal/messages', { replace: true }),
  });

  const footerLine = <p className="mt-2 text-center text-[12px]" style={{ color: MUTED }}>{menu.data?.footer}</p>;

  // MS-004: a plan waiting for a decision is asked about on the plan page.
  if (topic?.planQuestionsFor) {
    return (
      <Screen title={topic.label} onBack={() => setTopicKey(null)} testId="message-plan-redirect" footer={<button type="button" className={primary} onClick={() => navigate('/portal/plan')}>Go to my plan</button>}>
        <p className="mt-2 text-[15px] leading-normal" style={{ color: NAVY }}>
          Your care team sent you a plan that is waiting for your decision. Ask about it on the plan page, under I have a question, so your question arrives with the plan.
        </p>
      </Screen>
    );
  }

  if (topic && (composing || topic.questions.length === 0)) {
    const prefill = question?.question ?? null;
    return (
      <Screen
        title="Message your care team"
        onBack={() => (composing ? setComposing(false) : setTopicKey(null))}
        testId="message-compose"
        footer={
          <>
            {send.error && <p className="mb-2 text-[13px] text-alert" role="alert">{send.error instanceof Error ? send.error.message : 'Could not send'}</p>}
            <button type="button" className={primary} disabled={send.isPending || !body.trim()} onClick={() => send.mutate()}>
              Send to my care team
            </button>
            {footerLine}
          </>
        }
      >
        {topic.banner && (
          <p className="mb-3 rounded-[12px] px-3 py-2.5 text-[14px] font-semibold leading-snug" role="alert" style={{ background: '#fbe4e1', color: ALERT }} data-testid="emergency-banner">
            {topic.banner}
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          <span className="rounded-[6px] px-2 py-1 text-[12px] font-semibold" style={{ background: FOREST_SOFT, color: '#1b3f35' }}>{topic.label}</span>
          {prefill && prefill !== topic.label && <span className="rounded-[6px] px-2 py-1 text-[12px]" style={{ background: '#eeede8', color: NAVY }}>{prefill}</span>}
        </div>
        <label htmlFor="message-body" className="mt-4 block text-[14px] font-semibold" style={{ color: NAVY }}>Your message</label>
        <textarea
          id="message-body"
          className="mt-2 h-[140px] w-full resize-none rounded-[12px] border bg-white px-3 py-[10px] text-[14.5px] outline-none focus:border-forest"
          style={{ borderColor: '#d8d3c8' }}
          maxLength={4000}
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        {topic.photos && <PhotoPicker photos={photos} onChange={setPhotos} />}
        <label className="mt-3 flex items-center gap-2 text-[14px]" style={{ color: NAVY }}>
          <input type="checkbox" checked={call} onChange={(e) => setCall(e.target.checked)} className="h-4 w-4 accent-[var(--forest)]" />
          Please call me instead
        </label>
        <p className="mt-3 rounded-[12px] px-3 py-2.5 text-[13px] leading-normal" style={{ background: FOREST_SOFT, color: '#1b3f35' }}>
          Your care team sees your plan with this message. Please don't type card numbers here.
        </p>
      </Screen>
    );
  }

  if (question) {
    return (
      <Screen
        title="Your answer"
        onBack={() => setQuestionId(null)}
        testId="message-instant-answer"
        footer={
          question.answer ? (
            <div className="flex flex-col gap-[10px]">
              <p className="text-center text-[14px] font-semibold" style={{ color: NAVY }}>Did this answer your question?</p>
              <button type="button" className={primary} disabled={helpful.isPending} onClick={() => helpful.mutate()}>That answers it</button>
              <button type="button" className={secondary} style={{ borderColor: NAVY, color: NAVY }} onClick={() => setComposing(true)}>Message my care team</button>
            </div>
          ) : (
            <button type="button" className={primary} onClick={() => setComposing(true)}>Message my care team</button>
          )
        }
      >
        <h2 className="font-display text-[23px] font-semibold leading-snug" style={{ color: NAVY }}>{question.question}</h2>
        <div className="mt-3 rounded-[14px] border bg-white p-[14px]" style={{ borderColor: LINE }}>
          <p className="text-[14.5px] leading-[1.55]" style={{ color: NAVY }}>{question.answer ?? 'Your care team will answer this one. Tap below to send it.'}</p>
        </div>
      </Screen>
    );
  }

  if (topic) {
    return (
      <Screen title={topic.label} onBack={() => setTopicKey(null)} testId="message-topic-questions">
        {topic.banner && (
          <p className="mb-3 rounded-[12px] px-3 py-2.5 text-[14px] font-semibold leading-snug" role="alert" style={{ background: '#fbe4e1', color: ALERT }} data-testid="emergency-banner">
            {topic.banner}
          </p>
        )}
        <p className="text-[12.5px]" style={{ color: MUTED }}>Common questions</p>
        <div className="mt-2 flex flex-col gap-[10px]">
          {topic.questions.map((q) => (
            <button key={q.id} type="button" onClick={() => setQuestionId(q.id)} className="flex min-h-[52px] items-center justify-between gap-2 rounded-[12px] border bg-white px-4 py-3 text-left text-[14.5px]" style={{ borderColor: LINE, color: NAVY }}>
              {q.question}
              <Chevron />
            </button>
          ))}
          <button
            type="button"
            onClick={() => {
              setQuestionId(null);
              setComposing(true);
            }}
            className="flex min-h-[52px] items-center justify-between gap-2 rounded-[12px] border border-dashed px-4 py-3 text-left text-[14.5px]"
            style={{ borderColor: '#c9c4b8', color: NAVY }}
          >
            Write my own message
            <Chevron />
          </button>
        </div>
      </Screen>
    );
  }

  return (
    <Screen title="New message" onBack={() => navigate('/portal/messages')} testId="message-topics">
      <h1 className="font-display text-[24px] font-semibold" style={{ color: NAVY }}>What is it about?</h1>
      {menu.isLoading && <p className="mt-3 text-[13px]" style={{ color: MUTED }}>Loading</p>}
      {menu.error && <p className="mt-3 text-[13px] text-alert">Could not load the topics.</p>}
      <div className="mt-3 flex flex-col gap-[10px]">
        {menu.data?.topics.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTopicKey(t.key)}
            className="flex min-h-[64px] items-center gap-3 rounded-[12px] border px-3 py-3 text-left"
            style={t.clinical ? { borderColor: '#f2c9c3', background: '#fdf6f5' } : { borderColor: LINE, background: '#fff' }}
          >
            <TopicIcon topic={t} />
            <span className="min-w-0 flex-1">
              <span className="block text-[15px] font-semibold" style={{ color: NAVY }}>{t.label}</span>
              {t.clinical ? (
                <span className="block text-[12.5px]" style={{ color: MUTED }}>Goes straight to a provider</span>
              ) : (
                t.sub && <span className="block text-[12.5px]" style={{ color: MUTED }}>{t.sub}</span>
              )}
            </span>
            <Chevron />
          </button>
        ))}
      </div>
    </Screen>
  );
}

/* ------------------------------------------------------------------ *
 * S-11: the conversation
 * ------------------------------------------------------------------ */

export function PortalConversation() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const thread = usePortalQuery<ThreadView & { footer: string }>(`message-thread-${id}`, `/messages/${id}`);
  const [body, setBody] = useState('');
  const [photos, setPhotos] = useState<Photo[]>([]);
  const send = useMutation({
    mutationFn: () => portalRequest<ThreadView>(`/messages/${id}/reply`, { method: 'POST', body: { body, photoIds: photos.map((p) => p.id) } }),
    onSuccess: () => {
      setBody('');
      setPhotos([]);
      void qc.invalidateQueries({ queryKey: ['portal'] });
    },
  });
  const t = thread.data;
  return (
    <Screen
      title={t?.topic ?? 'Conversation'}
      onBack={() => navigate('/portal/messages')}
      testId="portal-conversation"
      footer={
        t?.canReply ? (
          <>
            {send.error && <p className="mb-2 text-[13px] text-alert" role="alert">{send.error instanceof Error ? send.error.message : 'Could not send'}</p>}
            {t.photos && <div className="mb-2"><PhotoPicker photos={photos} onChange={setPhotos} /></div>}
            <div className="flex items-end gap-2">
              <textarea
                aria-label="Reply to your care team"
                placeholder="Reply to your care team"
                className="max-h-[120px] min-h-[46px] flex-1 resize-none rounded-[23px] border bg-white px-4 py-[11px] text-[14.5px] outline-none focus:border-forest"
                style={{ borderColor: '#d8d3c8' }}
                rows={1}
                maxLength={4000}
                value={body}
                onChange={(e) => setBody(e.target.value)}
              />
              <button type="button" aria-label="Send" disabled={!body.trim() || send.isPending} onClick={() => send.mutate()} className="grid h-[46px] w-[46px] shrink-0 place-items-center rounded-full bg-forest text-white disabled:opacity-50">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m22 2-7 20-4-9-9-4Z" /><path d="M22 2 11 13" /></svg>
              </button>
            </div>
            <p className="mt-2 text-center text-[12px]" style={{ color: MUTED }}>{t.footer}</p>
          </>
        ) : t ? (
          <>
            <p className="text-center text-[14px]" style={{ color: NAVY }}>This conversation is closed.</p>
            <Link to="/portal/messages/new" className={`${primary} mt-2`}>Start a new message</Link>
          </>
        ) : undefined
      }
    >
      {thread.isLoading && <p className="text-[13px]" style={{ color: MUTED }}>Loading</p>}
      {thread.error && <p className="text-[13px] text-alert">Could not load this conversation.</p>}
      <div className="flex flex-col gap-3 pt-2" data-testid="conversation">
        {t?.messages.map((m, index) => {
          const mine = m.from === 'You';
          const system = m.from === 'MCOS';
          return (
            <div key={`${m.at}-${index}`} className={`flex flex-col ${mine ? 'items-end' : system ? 'items-center' : 'items-start'}`}>
              <div
                className={`max-w-[85%] whitespace-pre-line rounded-[14px] px-4 py-2.5 text-[14.5px] leading-snug ${mine ? 'bg-forest text-white' : 'border bg-white'}`}
                style={mine ? undefined : system ? { borderColor: LINE, color: MUTED } : { borderColor: LINE, color: NAVY }}
              >
                {m.body}
              </div>
              <MessagePhotos photos={m.photos} />
              <span className="mt-1 text-[11.5px]" style={{ color: MUTED }}>{mine ? `You · ${time(m.at)}` : `${m.from} · ${time(m.at)}`}</span>
            </div>
          );
        })}
        {t && (
          <span className="self-center rounded-full px-3 py-1 text-[12.5px]" style={{ background: FOREST_SOFT, color: '#1b3f35' }}>{t.status}</span>
        )}
      </div>
    </Screen>
  );
}
